import React, { useEffect, useMemo, useState } from 'react';
import { Cpu, Loader2 } from 'lucide-react';
import { apiService } from '../services/api';

// Consommation IA (admins) : où partent les tokens, par opération, fournisseur et modèle. Muse et Kev tournent sur le
// Mac mini (coût nul) ; le coût des autres modèles n'est estimé que si leur prix est renseigné (AI_PRICES_EUR).
const PERIODS = [[1, "Aujourd'hui"], [7, '7 jours'], [30, '30 jours'], [90, '90 jours']];
const COLORS = { Scaleway: 'bg-violet-500', Anthropic: 'bg-orange-500', OpenAI: 'bg-emerald-500', 'Mac mini': 'bg-sky-500' };

const tokens = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(2)} M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)} k` : String(n || 0));
const euros = (value, known = true) => (value == null || !known ? 'prix inconnu'
  : value === 0 ? '0 €' : `${value < 0.01 ? value.toFixed(4) : value.toFixed(2)} €`);
const duration = (s) => (!s ? '' : s >= 3600 ? `${(s / 3600).toFixed(1)} h` : s >= 60 ? `${Math.round(s / 60)} min` : `${Math.round(s)} s`);

const AIUsagePage = () => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null);
    setError(null);
    apiService.get(`/ai-usage?days=${days}`).then(setData).catch((err) => setError(err.message));
  }, [days]);

  const daily = useMemo(() => {
    const byDay = new Map();
    (data?.daily || []).forEach((row) => {
      if (!byDay.has(row.day)) byDay.set(row.day, { day: row.day, total: 0, parts: [] });
      const entry = byDay.get(row.day);
      entry.total += row.tokens;
      entry.parts.push(row);
    });
    const rows = [...byDay.values()];
    const max = Math.max(1, ...rows.map((r) => r.total));
    return rows.map((r) => ({ ...r, width: (100 * r.total) / max }));
  }, [data]);

  const total = (data?.operations || []).reduce((sum, r) => sum + r.input_tokens + r.output_tokens, 0);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center text-xl font-semibold text-gray-900">
          <Cpu className="h-5 w-5 mr-2 text-blue-600" />
          Consommation IA
        </h1>
        <div className="flex rounded-md border border-gray-200 bg-white text-sm">
          {PERIODS.map(([value, label]) => (
            <button key={value} onClick={() => setDays(value)}
              className={`px-3 py-1.5 ${days === value ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {!data && !error && <Loader2 className="h-5 w-5 animate-spin text-gray-400" />}

      {data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {data.providers.map((p) => (
              <div key={p.provider} className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex items-center text-sm font-medium text-gray-700">
                  <span className={`mr-2 h-2.5 w-2.5 rounded-full ${COLORS[p.provider] || 'bg-gray-400'}`} />
                  {p.provider}
                </div>
                <div className="mt-1 text-2xl font-semibold text-gray-900">{tokens(p.input_tokens + p.output_tokens)}</div>
                <div className="text-xs text-gray-500">
                  {tokens(p.input_tokens)} entrée · {tokens(p.output_tokens)} sortie · {p.calls} appels
                </div>
                <div className="mt-1 text-sm text-gray-700">{euros(p.cost_eur, p.cost_known)}</div>
              </div>
            ))}
            {!data.providers.length && <p className="text-sm text-gray-500">Rien d'enregistré sur la période.</p>}
          </div>

          {daily.length > 1 && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h2 className="mb-3 text-sm font-medium text-gray-700">Par jour</h2>
              <div className="space-y-1.5">
                {daily.map((d) => (
                  <div key={d.day} className="flex items-center gap-2 text-xs">
                    <span className="w-20 shrink-0 text-gray-500">{d.day.slice(5).split('-').reverse().join('/')}</span>
                    <div className="flex h-3 flex-1 overflow-hidden rounded bg-gray-100">
                      <div className="flex h-full" style={{ width: `${d.width}%` }}>
                        {d.parts.map((part) => (
                          <div key={part.provider} title={`${part.provider} : ${tokens(part.tokens)}`}
                            className={COLORS[part.provider] || 'bg-gray-400'}
                            style={{ width: `${(100 * part.tokens) / (d.total || 1)}%` }} />
                        ))}
                      </div>
                    </div>
                    <span className="w-16 shrink-0 text-right text-gray-600">{tokens(d.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Opération</th>
                  <th className="px-3 py-2">Modèle</th>
                  <th className="px-3 py-2 text-right">Appels</th>
                  <th className="px-3 py-2 text-right">Entrée</th>
                  <th className="px-3 py-2 text-right">dont cache</th>
                  <th className="px-3 py-2 text-right">Sortie</th>
                  <th className="px-3 py-2 text-right">Part</th>
                  <th className="px-3 py-2 text-right">Temps</th>
                  <th className="px-3 py-2 text-right">Coût</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.operations.map((r) => (
                  <tr key={`${r.operation}|${r.provider}|${r.model}`}>
                    <td className="px-3 py-2 text-gray-900">{r.operation}</td>
                    <td className="px-3 py-2 text-gray-600">
                      <span className={`mr-1.5 inline-block h-2 w-2 rounded-full ${COLORS[r.provider] || 'bg-gray-400'}`} />
                      {r.model}
                    </td>
                    <td className="px-3 py-2 text-right">{r.calls}</td>
                    <td className="px-3 py-2 text-right">{tokens(r.input_tokens)}</td>
                    <td className="px-3 py-2 text-right text-gray-500">{r.cached_tokens ? tokens(r.cached_tokens) : ''}</td>
                    <td className="px-3 py-2 text-right">{tokens(r.output_tokens)}</td>
                    <td className="px-3 py-2 text-right text-gray-500">
                      {total ? `${Math.round((100 * (r.input_tokens + r.output_tokens)) / total)} %` : ''}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-500">{duration(r.seconds)}</td>
                    <td className="px-3 py-2 text-right">{euros(r.cost_eur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500">
            Depuis le {data.since.split('-').reverse().join('/')}. Muse et Kev tournent sur le Mac mini : pas de coût
            à l'appel (temps de calcul affiché). Les coûts des autres modèles sont estimés à partir des prix
            renseignés dans AI_PRICES_EUR (€ par million de tokens, entrée / sortie)
            {data.priced_models.length ? ` : ${data.priced_models.join(', ')}` : ' (aucun pour l\'instant)'}.
          </p>
        </>
      )}
    </div>
  );
};

export default AIUsagePage;
